/**
 * Calendar change set — executive before/after delta for time-phased levers.
 * Answers: what moved, which teams must reconfirm, where overload appeared.
 */

import type {
  BucketUtilization,
  CalendarChangeSet,
  CalendarLaunchDelta,
  CalendarMoneyPeriodDelta,
  CalendarReconfirmRequest,
  CalendarTeamOverloadDelta,
  PlacementStatus,
  ScenarioDiffEntry,
  TimePhasedPlacement,
} from './timePhasedPlanning.types';
import { canonicalizeResourceType } from './timePhasedPlanning.types';
import type { BucketMoneyUtilization, FundingAffordability } from './timePhasedFinancial.types';

const GTM_TEAMS = new Set(['marketing', 'legal', 'design', 'support', 'finance']);

function weeksBetween(a?: string, b?: string): number | undefined {
  if (!a || !b) return undefined;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000));
}

function teamsFromPlacement(p: TimePhasedPlacement): string[] {
  const fromRationale = p.rationale?.teams;
  if (Array.isArray(fromRationale) && fromRationale.length > 0) {
    return [
      ...new Set(
        fromRationale
          .map((t) => canonicalizeResourceType(String(t)))
          .filter((t) => GTM_TEAMS.has(t) || t !== 'engineering'),
      ),
    ];
  }
  return ['marketing', 'legal', 'design', 'support'];
}

function classifyDelta(
  b: TimePhasedPlacement,
  h: TimePhasedPlacement,
): CalendarLaunchDelta['kind'] | null {
  const startChanged = b.feasibleStart !== h.feasibleStart || b.feasibleEnd !== h.feasibleEnd;
  const statusChanged = b.placementStatus !== h.placementStatus;
  const launchChanged = b.launchable !== h.launchable;
  if (!startChanged && !statusChanged && !launchChanged) return null;
  if (b.launchable && !h.launchable) return 'displaced';
  if (startChanged) return 'moved';
  if (launchChanged) return 'launchability_changed';
  return 'status_changed';
}

/** Compatibility row for older scenario_diffs payloads. */
export function buildScenarioDiffEntries(
  base: TimePhasedPlacement[],
  hypothesis: TimePhasedPlacement[],
): ScenarioDiffEntry[] {
  return buildCalendarChangeSet({
    basePlacements: base,
    hypothesisPlacements: hypothesis,
    baseUtilization: [],
    hypothesisUtilization: [],
    lever: 'promote',
    leverPriorityId: '',
    leverPriorityName: '',
    baseScenarioId: '',
    hypothesisScenarioId: '',
  }).entries;
}

export function buildCalendarChangeSet(input: {
  basePlacements: TimePhasedPlacement[];
  hypothesisPlacements: TimePhasedPlacement[];
  baseUtilization: BucketUtilization[];
  hypothesisUtilization: BucketUtilization[];
  lever: 'promote' | 'delay';
  leverPriorityId: string;
  leverPriorityName: string;
  delayWeeks?: number;
  baseScenarioId: string;
  hypothesisScenarioId: string;
  createdAt?: string;
  baseMoneyUtilization?: BucketMoneyUtilization[];
  hypothesisMoneyUtilization?: BucketMoneyUtilization[];
  hypothesisAffordability?: FundingAffordability | null;
}): CalendarChangeSet {
  const baseMap = new Map(input.basePlacements.map((p) => [p.priorityId, p]));
  const launchDeltas: CalendarLaunchDelta[] = [];
  const reconfirm: CalendarReconfirmRequest[] = [];
  const entries: ScenarioDiffEntry[] = [];

  for (const h of input.hypothesisPlacements) {
    const b = baseMap.get(h.priorityId);
    if (!b) continue;
    const kind = classifyDelta(b, h);
    if (!kind) continue;

    const deltaStartWeeks = weeksBetween(b.feasibleStart, h.feasibleStart);
    const affectedTeams = [
      ...new Set([...teamsFromPlacement(b), ...teamsFromPlacement(h)]),
    ];

    launchDeltas.push({
      priorityId: h.priorityId,
      priorityName: h.priorityName,
      kind,
      baseFeasibleStart: b.feasibleStart,
      baseFeasibleEnd: b.feasibleEnd,
      hypothesisFeasibleStart: h.feasibleStart,
      hypothesisFeasibleEnd: h.feasibleEnd,
      deltaStartWeeks,
      baseStatus: b.placementStatus,
      hypothesisStatus: h.placementStatus,
      baseLaunchable: b.launchable,
      hypothesisLaunchable: h.launchable,
      affectedTeams,
    });

    entries.push({
      priorityId: h.priorityId,
      priorityName: h.priorityName,
      baseStatus: b.placementStatus,
      hypothesisStatus: h.placementStatus,
      deltaStartWeeks,
      affectedTeams,
    });

    const windowMoved =
      b.feasibleStart !== h.feasibleStart ||
      b.feasibleEnd !== h.feasibleEnd ||
      b.targetLaunchStart !== h.targetLaunchStart;
    const lostLaunchable = b.launchable && !h.launchable;
    if (windowMoved || lostLaunchable || kind === 'displaced') {
      for (const team of affectedTeams.filter((t) => GTM_TEAMS.has(t))) {
        reconfirm.push({
          priorityId: h.priorityId,
          priorityName: h.priorityName,
          team,
          reason: lostLaunchable
            ? 'Launch became not launchable — confirm or renegotiate participation'
            : windowMoved
              ? 'Launch window moved — confirm participation still holds'
              : 'Placement status changed — reconfirm capacity commitment',
          windowStart: h.feasibleStart ?? h.targetLaunchStart,
          windowEnd: h.feasibleEnd ?? h.targetLaunchEnd,
        });
      }
    }
  }

  const overloadDeltas = diffOverloads(input.baseUtilization, input.hypothesisUtilization);
  const newlyNotLaunchable = launchDeltas.filter(
    (d) => d.baseLaunchable && !d.hypothesisLaunchable,
  ).length;
  const moneyPeriodDeltas = diffMoneyPeriods(
    input.baseMoneyUtilization ?? [],
    input.hypothesisMoneyUtilization ?? [],
  );
  const newFundingOverageBuckets = moneyPeriodDeltas.filter(
    (d) => d.hypothesisOverage > 0.01 && d.hypothesisOverage > d.baseOverage + 0.01,
  ).length;

  return {
    version: 1,
    createdAt: input.createdAt ?? new Date().toISOString(),
    lever: input.lever,
    leverPriorityId: input.leverPriorityId,
    leverPriorityName: input.leverPriorityName,
    delayWeeks: input.delayWeeks,
    baseScenarioId: input.baseScenarioId,
    hypothesisScenarioId: input.hypothesisScenarioId,
    summary: {
      launchesChanged: launchDeltas.length,
      reconfirmCount: reconfirm.length,
      newOverloadBuckets: overloadDeltas.length,
      newlyNotLaunchable,
      newFundingOverageBuckets,
      fundingOverageTotal: input.hypothesisAffordability?.overageTotal,
    },
    launchDeltas,
    reconfirm,
    overloadDeltas,
    moneyPeriodDeltas,
    fundingContributors: input.hypothesisAffordability?.contributingPriorities?.slice(0, 8),
    entries,
  };
}

function diffMoneyPeriods(
  base: BucketMoneyUtilization[],
  hypothesis: BucketMoneyUtilization[],
): CalendarMoneyPeriodDelta[] {
  const baseMap = new Map(base.map((b) => [b.bucketStart, b]));
  const out: CalendarMoneyPeriodDelta[] = [];
  for (const h of hypothesis) {
    const b = baseMap.get(h.bucketStart);
    const baseDemand = b?.forecastDemand ?? 0;
    const baseOverage = b?.overage ?? 0;
    const delta = h.forecastDemand - baseDemand;
    if (Math.abs(delta) < 1 && Math.abs(h.overage - baseOverage) < 1) continue;
    out.push({
      bucketStart: h.bucketStart,
      baseDemand: Math.round(baseDemand),
      hypothesisDemand: Math.round(h.forecastDemand),
      delta: Math.round(delta),
      baseOverage: Math.round(baseOverage),
      hypothesisOverage: Math.round(h.overage),
    });
  }
  return out
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 16);
}

function diffOverloads(
  base: BucketUtilization[],
  hypothesis: BucketUtilization[],
): CalendarTeamOverloadDelta[] {
  const baseMap = new Map(
    base.map((b) => [`${canonicalizeResourceType(b.resourceType)}::${b.bucketStart}`, b]),
  );
  const out: CalendarTeamOverloadDelta[] = [];
  for (const h of hypothesis) {
    const rt = canonicalizeResourceType(h.resourceType);
    if (!GTM_TEAMS.has(rt) && rt !== 'engineering') continue;
    const b = baseMap.get(`${rt}::${h.bucketStart}`);
    const basePct = b?.utilizationPct ?? 0;
    // Newly hot (≥85%) or jumped ≥20pp into overload territory
    if (h.utilizationPct >= 85 && (basePct < 85 || h.utilizationPct - basePct >= 20)) {
      out.push({
        resourceType: rt,
        bucketStart: h.bucketStart,
        baseUtilizationPct: Math.round(basePct),
        hypothesisUtilizationPct: Math.round(h.utilizationPct),
      });
    }
  }
  return out.slice(0, 24);
}

export function isCalendarChangeSet(value: unknown): value is CalendarChangeSet {
  if (!value || typeof value !== 'object') return false;
  const v = value as CalendarChangeSet;
  return v.version === 1 && Array.isArray(v.launchDeltas) && Array.isArray(v.reconfirm);
}

/** Normalize legacy array-only diff_payload into a minimal change set. */
export function coerceChangeSet(
  payload: unknown,
  meta?: Partial<CalendarChangeSet>,
): CalendarChangeSet | null {
  if (isCalendarChangeSet(payload)) return payload;
  if (!Array.isArray(payload)) return null;
  const entries = payload as ScenarioDiffEntry[];
  return {
    version: 1,
    createdAt: meta?.createdAt ?? new Date().toISOString(),
    lever: meta?.lever ?? 'promote',
    leverPriorityId: meta?.leverPriorityId ?? '',
    leverPriorityName: meta?.leverPriorityName ?? '',
    delayWeeks: meta?.delayWeeks,
    baseScenarioId: meta?.baseScenarioId ?? '',
    hypothesisScenarioId: meta?.hypothesisScenarioId ?? '',
    summary: {
      launchesChanged: entries.length,
      reconfirmCount: 0,
      newOverloadBuckets: 0,
      newlyNotLaunchable: 0,
    },
    launchDeltas: entries.map((e) => ({
      priorityId: e.priorityId,
      priorityName: e.priorityName,
      kind: 'status_changed' as const,
      deltaStartWeeks: e.deltaStartWeeks,
      baseStatus: e.baseStatus as PlacementStatus,
      hypothesisStatus: e.hypothesisStatus as PlacementStatus,
      baseLaunchable: true,
      hypothesisLaunchable: true,
      affectedTeams: e.affectedTeams ?? [],
    })),
    reconfirm: [],
    overloadDeltas: [],
    entries,
  };
}
